<template>
  <main class="bg-about-background bg-top pb-20 bg-[length:130rem] h-full bg-no-repeat bg-white pt-28">
    <section class="max-w-4xl mx-auto">
      <div class="mb-6 flex flex-col gap-2">
        <div class="flex items-center gap-2 text-sm font-medium text-gray-500">
          <UserCircleIcon class="h-4 w-4 text-primary" />
          My Account
        </div>
        <h2 class="text-3xl font-semibold text-gray-900">
          Account Overview
        </h2>
        <p class="text-gray-600">
          Review your membership, billing, and activity.
        </p>
      </div>

      <div class="grid gap-6 md:grid-cols-2">
        <div class="bg-white border border-gray-200 rounded-2xl shadow-sm p-6">
          <div class="flex items-center justify-between">
            <h3 class="text-sm font-medium text-gray-600">
              Personal Information
            </h3>
            <div class="text-xs text-gray-500 flex items-center gap-1">
              <CalendarDaysIcon class="h-4 w-4" />
              Member since {{ formatDate(user?.createdAt) }}
            </div>
          </div>
          <div class="mt-4 flex items-center gap-4">
            <div class="h-12 w-12 rounded-full bg-[#f6f1ec] flex items-center justify-center text-sm font-semibold text-gray-700">
              {{ user?.fullName?.[0] || 'N' }}
            </div>
            <div>
              <div class="text-xl font-semibold text-gray-900">
                {{ user?.fullName }}
              </div>
              <div class="text-sm text-gray-600">
                {{ user?.email }}
              </div>
            </div>
          </div>
          <div class="mt-4 text-sm text-gray-600">
            Company: <span class="font-medium text-gray-900">{{ user?.company || '—' }}</span>
          </div>
        </div>

        <div class="bg-primary/10 border border-primary/30 rounded-2xl shadow-sm p-6">
          <div class="flex items-center justify-between">
            <h3 class="text-sm font-medium text-gray-600">
              Billing Details
            </h3>
            <CreditCardIcon class="h-5 w-5 text-primary" />
          </div>
          <div class="mt-4">
            <div class="text-2xl font-semibold text-gray-900">
              {{ planName }}
            </div>
            <div class="text-sm text-gray-700 mt-1">
              {{ planPrice }}
            </div>
            <div class="mt-3 inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-xs font-medium text-gray-700">
              <CheckBadgeIcon class="h-4 w-4 text-primary" />
              {{ planDescription }}
            </div>
          </div>
          <div class="mt-5 flex items-center justify-between">
            <div class="text-xs text-gray-600">
              Next billing date: N/A
            </div>
            <button
              class="inline-flex items-center rounded-full bg-primary px-4 py-2 text-sm font-semibold text-gray-900 hover:bg-[#c9ad99]"
              type="button"
            >
              Change Plan
            </button>
          </div>
        </div>

        <div class="bg-white border border-gray-200 rounded-2xl shadow-sm p-6 md:col-span-2">
          <div class="flex items-center justify-between">
            <h3 class="text-sm font-medium text-gray-600">
              Activity
            </h3>
            <router-link
              class="inline-flex items-center gap-2 rounded-full border border-gray-300 px-3 py-1 text-xs font-medium text-gray-700 hover:bg-gray-100"
              to="/dashboard/settings"
            >
              <Cog6ToothIcon class="h-4 w-4" />
              Settings
            </router-link>
          </div>
          <div class="mt-4 grid gap-3 sm:grid-cols-2 text-sm text-gray-700">
            <div class="flex items-center justify-between rounded-xl border border-gray-200 px-4 py-3">
              <span class="text-gray-500">Last active</span>
              <span class="font-medium text-gray-900">{{ formatDate(user?.updatedAt) }}</span>
            </div>
            <div class="flex items-center justify-between rounded-xl border border-gray-200 px-4 py-3">
              <span class="text-gray-500">Membership status</span>
              <span class="font-medium text-gray-900">{{ planName }}</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  </main>
</template>
<script lang="ts">
import {computed, defineComponent, inject, watchEffect} from 'vue';
import {useRouter} from 'vue-router';
import {userKey} from "@/composables/useUser";
import {settingsKey} from "@/composables/useSettings";
import {useHead} from "@vueuse/head";
import {
  CalendarDaysIcon,
  CheckBadgeIcon,
  Cog6ToothIcon,
  CreditCardIcon,
  UserCircleIcon,
} from "@heroicons/vue/24/outline";

export default defineComponent({
  name: "Account",
  components: {
    CalendarDaysIcon,
    CheckBadgeIcon,
    Cog6ToothIcon,
    CreditCardIcon,
    UserCircleIcon,
  },
  setup() {
    const {user} = inject(userKey)!;
    const {settings} = inject(settingsKey)!;
    const router = useRouter();

    const planName = computed(() => {
      return user.value?.membershipPlan === 'community_plus'
        ? 'Community Plus'
        : 'Community';
    });

    const planPrice = computed(() => {
      return user.value?.membershipPlan === 'community_plus'
        ? '£0.00/month'
        : '£0.00/month';
    });

    const planDescription = computed(() => {
      return user.value?.membershipPlan === 'community_plus'
        ? 'Community Plus access unlocked for courses and directory.'
        : 'Community access with events only. Upgrade to Community Plus for Academy and Directory.';
    });

    const formatDate = (value?: string): string => {
      if (!value) return '—';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return '—';
      return new Intl.DateTimeFormat('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      }).format(date);
    };

    watchEffect(() => {
      if (settings.value) {
        useHead({
          title: 'My Account',
          meta: [
            {name: 'description', content: '...'},
            {name: 'keywords', content: '...'},
            {name: 'author', content: '...'},
          ],
        });
      }
    });

    return {user, settings, router, formatDate, planName, planPrice, planDescription};
  },
});
</script>
